import { ROLE_SUPER_ADMIN } from "@/src/config";
import client2 from "@/src/server/clients/tri-company-client";
import client1 from "@/src/server/clients/tri-user-client";
import { User } from "@/src/store/type";
import { clientAccessTokenCookieOptions } from "@/src/util/access-token-cookie";
import {
  invalidateAuthSession,
  isAccessTokenExpired,
  isAuthSessionInvalidated,
  refreshAccessToken,
  registerAuthRefreshHandler,
  resetAuthSessionInvalidation,
} from "@/src/util/auth-session";
import indexedDBStorage from "@/src/util/indexed-db-storage";
import { coerceModuleList, coercePermissionList } from "@/src/util/permission";
import { resetClientSessionState, revokeAuthTokens } from "@/src/util/reset-client-session";
import { deleteCookie, getCookie, setCookie } from "cookies-next/client";
import * as jose from "jose";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

interface AuthState {
  user?: User;
  hydrated: boolean;
  update: () => Promise<boolean>;
  syncFromAccessToken: (accessToken: string) => void;
  bootstrapSession: () => Promise<boolean>;
  set: (accessToken: string, refreshToken: string, rememberMe?: boolean) => Promise<boolean>;
  clear: () => Promise<void>;
}

export const store = create<AuthState>()(
  persist(
    (set, get) => ({
      hydrated: false,
      syncFromAccessToken: (accessToken: string) => {
        try {
          const decoded = jose.decodeJwt(accessToken) as User;
          const current = get().user;
          const roles = Array.isArray(decoded.roles)
            ? decoded.roles.filter((r): r is string => typeof r === "string")
            : (current?.roles ?? []);
          // Always take roles/permissions/modules from JWT (refresh recomputes them on BE).
          // Keep profile fields from the last getUserById when present.
          set({
            user: {
              ...decoded,
              name:
                current?.name ??
                decoded.name ??
                (decoded as { username?: string }).username,
              tel: current?.tel,
              mobile: current?.mobile,
              title: current?.title,
              department: current?.department,
              signature: current?.signature,
              status: current?.status,
              roles,
              permissions: coercePermissionList(decoded.permissions),
              modules: coerceModuleList(decoded.modules),
              isApproved:
                roles.includes(ROLE_SUPER_ADMIN) ||
                decoded.companySeqNumber === 0 ||
                (decoded as { isApproved?: boolean }).isApproved === true ||
                (current?.isApproved ?? false),
              isSuperAdmin: roles.includes(ROLE_SUPER_ADMIN),
              portOfStation: current?.portOfStation,
            },
          });
        } catch (err) {
          console.warn("[Auth] syncFromAccessToken failed", err);
        }
      },
      bootstrapSession: async () => {
        if (isAuthSessionInvalidated()) {
          return false;
        }

        let token = getCookie("access-token") as string | undefined;
        const needsFreshClaims = (accessToken: string) => {
          try {
            const decoded = jose.decodeJwt(accessToken) as User;
            // Old tokens lack `modules` — force refresh so menu matches Role.modules.
            return !Array.isArray(decoded.modules);
          } catch {
            return true;
          }
        };

        if (!token || isAccessTokenExpired(token) || needsFreshClaims(token)) {
          if (token && isAccessTokenExpired(token)) {
            deleteCookie("access-token");
          }
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            // Still allow sync of a valid (non-expired) access token without modules
            // so Super Admin / public routes can work; menu may be empty until re-login.
            if (!token || isAccessTokenExpired(token)) {
              invalidateAuthSession();
              return false;
            }
          } else {
            token = getCookie("access-token") as string | undefined;
            if (!token || isAccessTokenExpired(token)) {
              invalidateAuthSession();
              return false;
            }
          }
        }

        try {
          // Always re-sync JWT claims so refresh-updated modules/permissions hit the sidebar.
          get().syncFromAccessToken(token);
          void get().update();
          return true;
        } catch (err) {
          console.warn("[Auth] bootstrapSession invalid token", err);
          invalidateAuthSession();
          return false;
        }
      },
      update: async () => {
        return new Promise(async (resolve, reject) => {
          const user = get().user;
          if (!user) {
            reject(false);
            return;
          }
          const [res1, res2] = await Promise.allSettled([
            client1.getUserById({ id: user.userId }),
            client2.getCompanyBySeqNumber({ seqNumber: user.companySeqNumber }),
          ]);
          const userData = res1.status === "fulfilled" && res1.value.success && res1.value.data
            ? res1.value.data.user
            : undefined;
          const companyData = res2.status === "fulfilled" && res2.value.data
            ? res2.value.data.company
            : undefined;
          if (userData) {
            const roles = Array.isArray(userData.roles)
              ? userData.roles.filter((r): r is string => typeof r === "string")
              : (user.roles ?? []);
            set({
              user: {
                ...user,
                name: userData.name,
                tel: userData.tel,
                mobile: userData.mobile,
                title: userData.title,
                department: userData.department,
                signature: userData.signature,
                status: userData.status,
                roles,
                // permissions/modules stay from JWT (refresh recomputes); profile from API
                isApproved:
                  roles.includes(ROLE_SUPER_ADMIN) ||
                  user.companySeqNumber === 0 ||
                  (companyData?.isApproved ?? false),
                isSuperAdmin: roles.includes(ROLE_SUPER_ADMIN),
                portOfStation: companyData?.portOfStation?.trim() || user.portOfStation,
              },
            });
            resolve(true);
          } else if (res1.status === "rejected") {
            reject(false);
          } else {
            resolve(false);
          }
        });
      },
      set: async (accessToken, refreshToken, rememberMe) => {
        return new Promise(async (resolve, reject) => {
          let decoded: User;
          try {
            decoded = jose.decodeJwt(accessToken) as User;
            if (process.env.NODE_ENV === "development") {
              console.debug("[DEBUG] decoded token ", decoded);
            }
          } catch (err) {
            console.error("[ERR] invalid token ", err);
            reject(false);
            return;
          }

          const previousUserId = get().user?.userId;
          if (previousUserId && previousUserId !== decoded.userId) {
            await resetClientSessionState();
          }

          setCookie("access-token", accessToken, clientAccessTokenCookieOptions(accessToken));

          const res1 = await fetch("/api/auth/tokens", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ accessToken, refreshToken, rememberMe }),
          });

          if (!res1.ok) {
            deleteCookie("access-token");
            reject(false);
            return;
          }

          const [res2, res3] = await Promise.allSettled([
            client1.getUserById({ id: decoded.userId }),
            client2.getCompanyBySeqNumber({ seqNumber: decoded.companySeqNumber }),
          ]);

          const userData = res2.status === "fulfilled" && res2.value.success && res2.value.data
            ? res2.value.data.user
            : undefined;
          const companyData = res3.status === "fulfilled" && res3.value.data
            ? res3.value.data.company
            : undefined;

          if (res2.status === "rejected") {
            console.warn("[Auth] getUserById failed, using JWT data only:", res2.reason);
          }
          if (res3.status === "rejected") {
            console.warn("[Auth] getCompanyBySeqNumber failed:", res3.reason);
          }

          const jwtRoles = Array.isArray(decoded.roles)
            ? decoded.roles.filter((r): r is string => typeof r === "string")
            : [];
          const roles = Array.isArray(userData?.roles)
            ? userData.roles.filter((r): r is string => typeof r === "string")
            : jwtRoles;
          set({
            user: {
              ...decoded,
              name: userData?.name ?? decoded.name ?? (decoded as { username?: string }).username,
              tel: userData?.tel,
              mobile: userData?.mobile,
              title: userData?.title,
              department: userData?.department,
              signature: userData?.signature,
              status: userData?.status,
              roles,
              permissions: coercePermissionList(decoded.permissions),
              modules: coerceModuleList(decoded.modules),
              isApproved:
                roles.includes(ROLE_SUPER_ADMIN) ||
                decoded.companySeqNumber === 0 ||
                (decoded as { isApproved?: boolean }).isApproved === true ||
                (companyData?.isApproved ?? false),
              isSuperAdmin: roles.includes(ROLE_SUPER_ADMIN),
              portOfStation: companyData?.portOfStation?.trim() || undefined,
            },
          });
          resetAuthSessionInvalidation();
          resolve(true);
        });
      },
      clear: async () => {
        if (process.env.NODE_ENV === "development") {
          console.debug(`[DEBUG] Clear auth`);
        }
        setCookie("last-visited", window.location.pathname + window.location.search);
        deleteCookie("access-token");
        set({ user: undefined });
        await revokeAuthTokens();
        await resetClientSessionState();
        await store.persist.clearStorage();
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => indexedDBStorage),
      partialize: (state) => {
        if (!state.user) {
          return { user: undefined };
        }
        const { exp, iat, ...rest } = state.user;
        return { user: rest };
      },
      onRehydrateStorage: () => (state) => {
        store.setState({ hydrated: true });
        console.debug("[DEBUG] auth store rehydrated", state?.user);

        if (typeof window === "undefined") {
          return;
        }

        const accessToken = getCookie("access-token");
        if (!accessToken) {
          return;
        }

        try {
          const decoded = jose.decodeJwt(accessToken) as User;
          if (isAccessTokenExpired(accessToken)) {
            return;
          }
          if (state?.user?.userId && decoded.userId !== state.user.userId) {
            console.warn("[Auth] persisted user does not match access token — clearing session");
            void store.getState().clear().then(() => {
              invalidateAuthSession();
            });
          }
        } catch (err) {
          console.warn("[Auth] invalid access token on rehydrate", err);
          void store.getState().clear().then(() => {
            invalidateAuthSession();
          });
        }
      },
    },
  ),
);

registerAuthRefreshHandler((accessToken) => {
  store.getState().syncFromAccessToken(accessToken);
});

export default store;
